import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet";
import { ArrowRight, BadgeCheck, FileText } from 'lucide-react';
import { motion } from 'framer-motion';
import {
    FaShieldAlt,        // For Zero Depreciation
    FaTools,            // For Engine & Gearbox Protection
    FaFileInvoiceDollar,// For Return to Invoice
    FaTruckPickup,      // For Roadside Assistance
    FaCarSide,          // For Tire & Rim Protection
    FaKey,              // For Key Replacement Cover
    FaCloudSunRain,
    FaUserLock,
    FaCarCrash,
} from "react-icons/fa";
import FAQSchema from "../../FAQSchema";
import AdBanner from "../AdPage/AdBanner";

function Icon({ id, open }) {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      fill="none"
      viewBox="0 0 24 24"
      strokeWidth={2}
      stroke="currentColor"
      className={`${id === open ? "rotate-180" : ""} h-5 w-5 transition-transform`}
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M19.5 8.25l-7.5 7.5-7.5-7.5"
      />
    </svg>
  );
}

const PorscheCarInsurance = () => {
  const [activeIndex, setActiveIndex] = useState(0);
  const [insuranceData, setInsuranceData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const faqs = [
    {
      question: "Is Porsche insurance more expensive than other luxury brands?",
      answer: "Yes, due to its performance orientation and higher repair costs.",
    },
    {
      question: "Can I buy Porsche insurance online?",
      answer: "Yes, most insurers offer secure online platforms for purchase and renewal.",
    },
    {
      question: "What does comprehensive Porsche insurance cover?",
      answer: "It includes own damage, third-party liability, theft, fire, and natural disasters.",
    },
    {
      question: "Is zero depreciation a must for Porsche?",
      answer: "Highly recommended, especially for first-time buyers or vehicles under 5 years old.",
    },
    {
      question: "Does Porsche offer insurance directly via dealerships?",
      answer: "Yes, but it's always wise to compare with independent aggregators for better value.",
    },
    {
      question: "Can I insure a used Porsche?",
      answer: "Yes, but it may require inspection and policy customization.",
    },
    {
      question: "Are electric Porsches like the Taycan insured differently?",
      answer: "Base coverage is the same, but EV-specific add-ons may be applicable.",
    },
    {
      question: "What’s the average claim settlement time for Porsche?",
      answer: "10–14 business days, depending on claim type and documentation.",
    },
    {
      question: "Is off-road damage covered under Porsche SUV insurance?",
      answer: "Only if specified under policy terms. Check exclusions before buying.",
    },
    {
      question: "What’s the fine for not insuring a Porsche in India?",
      answer: "₹2,000 for first offence and ₹4,000 for repeat offences.",
    },
    {
      question: "Can I transfer my existing policy to a new Porsche?",
      answer: "No, a fresh policy is needed unless it’s a direct replacement.",
    },
    {
      question: "What happens to insurance if I sell the Porsche?",
      answer: "You can transfer it to the new owner or cancel it with a refund request.",
    },
    {
      question: "Is IDV negotiable for Porsche cars?",
      answer: "Yes, within a range. Higher IDV means better claim amount but also higher premium.",
    },
    {
      question: "What is the best insurance plan for Porsche 911?",
      answer: "A comprehensive plan with zero-dep, tire protection, and RSA.",
    },
    {
      question: "Do Porsche owners get cashless garages?",
      answer: "Yes, through select insurers tied up with Porsche-approved service centers.",
    },
    {
      question: "Can I reduce my premium as my Porsche ages?",
      answer: "Yes, premiums tend to decline slightly with age but so does IDV.",
    },
    {
      question: "Should I include engine cover for a Porsche EV?",
      answer: "EVs don’t need traditional engine cover but consider EV-specific protection add-ons.",
    },
    {
      question: "Can I cancel Porsche insurance mid-term?",
      answer: "Yes, but only with valid documentation and potential deductions.",
    },
    {
      question: "What’s not covered in standard Porsche insurance?",
      answer: "Mechanical failure, wear and tear, or racing damages unless add-ons cover it.",
    },
    {
      question: "Is it safe to insure a Porsche through third-party brokers?",
      answer: "Yes, if they are IRDAI-registered and offer transparent policies.",
    },
  ];

  const toggleAccordion = (index) => {
    setActiveIndex(activeIndex === index ? null : index);
  };

  const cardsNew = [
    {
      title: "Zero Depreciation",
      description: "Ensures full claim amount without factoring in part depreciation.",
      icon: <FaShieldAlt className="text-4xl text-red-500 mb-4" />,
    },
    {
      title: "Return to Invoice",
      description: "Restores full invoice value in case of total loss or theft.",
      icon: <FaFileInvoiceDollar className="text-4xl text-orange-600 mb-4" />,
    },
    {
      title: "Engine & Gearbox Protection",
      description: "Critical for high-performance systems, especially in Indian road conditions.",
      icon: <FaTools className="text-4xl text-yellow-600 mb-4" />,
    },
    {
      title: "Tire & Rim Protection",
      description: "Covers costly rims and tires against accidental damage.",
      icon: <FaCarSide className="text-4xl text-indigo-600 mb-4" />,
    },
    {
      title: "Roadside Assistance",
      description: "Includes emergency towing, battery assistance, and fuel delivery.",
      icon: <FaTruckPickup className="text-4xl text-blue-500 mb-4" />,
    },
    {
      title: "Key Replacement Cover",
      description: "Especially relevant due to high-tech Porsche smart keys.",
      icon: <FaKey className="text-4xl text-purple-600 mb-4" />,
    },
  ];

  const features = [
    { icon: '🛡️', text: 'Reliable & Accurate Estimates' },
    { icon: '⏱️', text: 'Takes Less Than a Minute' },
    { icon: '📉', text: 'Instantly Shows Premium Breakup' },
    { icon: '📄', text: 'No Paperwork or Documentation' },
    { icon: '📲', text: 'Easy to Use on Any Device' },
    { icon: '💰', text: 'Helps You Save on Insurance' },
  ];
const porscheinsurance = [
    {
      name: "Macan",
      path: "/porsche-macan-car-insurance", 
      image: "/assets/porschemacan.jpeg",
    },
     {
      name: "Cayenne",
      path: "/porsche-cayenne-car-insurance", 
      image: "/assets/porschecayenne.jpeg",
    },
    {
      name: "Cayenne Coupe",
      path: "/porsche-cayennecoupe-car-insurance", 
      image: "/assets/porschecayennecoupe.jpeg",
    },
     {
      name: "Panamera",
      path: "/porsche-panamera-car-insurance", 
      image: "/assets/porschepanamera.jpeg",
    },
      {
      name: "911",
      path: "/porsche-911-car-insurance", 
      image: "/assets/porsche911.jpeg",
    },
    {
      name: "Taycan",
      path: "/porsche-taycan-car-insurance", 
      image: "/assets/porschetaycan.jpeg",
    },
      {
      name: "718",
      path: "/porsche-718-car-insurance", 
      image: "/assets/porsche718.jpeg",
    },
  ];
  return (
    <>
      <Helmet>
        <title>Porsche Car Insurance in India – Compare Quotes & Buy Online</title>
        <meta
          name="description"
          content="Explore Porsche car insurance options in India. Compare quotes, customize coverage, and buy online to protect your Porsche vehicle."
        />
        <meta
          name="keywords"
          content="porsche car insurance, luxury car insurance india, porsche 911 insurance, porsche taycan insurance, porsche cayenne insurance, car insurance quotes"
        />
           <link
          rel="canonical"
          href="https://thefinmaster.com/porsche-car-insurance"
        />
        <meta
          name="robots"
          content="max-snippet:-1, max-image-preview:standard"
        />
        <meta name="robots" content="max-video-preview:-1" />
        {/* <link rel="canonical" href="https://thefinmaster.com/porsche-car-insurance" />
        <meta name="robots" content="max-snippet:-1, max-image-preview:standard, max-video-preview:-1" />
        <meta property="og:title" content="Porsche Car Insurance in India" />
        <meta property="og:description" content="Compare and buy Porsche car insurance online with ease." />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://thefinmaster.com/porsche-car-insurance" />
        <meta name="twitter:card" content="summary_large_image" /> */}
      </Helmet>
      <FAQSchema faqs={faqs} />
      <div className="w-full flex justify-center items-center px-4 mt-20">
        <div className="w-full max-w-5xl h-auto object-contain rounded-lg shadow-md transition duration-300 hover:scale-105">
          <AdBanner displayOrder="1" />
        </div>
      </div>
      <nav className="text-gray-500 text-sm my-4 ml-10" aria-label="Breadcrumb">
        <ol className="list-none p-0 inline-flex">
          <li className="flex items-center">
            <Link to="/" className="text-blue-600 hover:text-blue-800">Home</Link>
            <svg
              className="w-4 h-4 mx-2 text-gray-500"
              fill="currentColor"
              viewBox="0 0 20 20"
            >
              <path
                fillRule="evenodd"
                d="M7.293 14.707a1 1 0 010-1.414L11.586 9 7.293 4.707a1 1 0 111.414-1.414l5 5a1 1 0 010 1.414l-5 5a1 1 0 01-1.414 0z"
                clipRule="evenodd"
              />
            </svg>
          </li>
          <li className="flex items-center">
            <span className="text-gray-500">Porsche Car Insurance</span>
          </li>
        </ol>
      </nav>
      <div className="w-full flex flex-col items-center">
        <div className="w-full flex justify-center">
          <div className="w-full sm:w-[95%] flex flex-col sm:flex-row justify-center items-center">
            <div className="w-full sm:w-[50%]">
              <div className="w-[90%]">
                <h1 className="text-3xl md:text-5xl font-bold mt-6 md:mt-0 md:pl-4 pl-6">
                  <span className="text-blue-600">Porsche Car Insurance Guide</span> Coverage, Premiums, Tips and FAQs
                </h1>
                <p className="text-lg md:text-base lg:text-lg mt-4 pl-4">
                  Porsche represents the pinnacle of automotive engineering, offering unmatched performance, luxury, and prestige. In India, owning a Porsche is more than just owning a car—it's a lifestyle. Whether it’s the iconic 911, the sporty Cayenne, or the all-electric Taycan, protecting your investment with the right insurance coverage is critical. This guide explores everything you need to know about insuring a Porsche in India—from coverage types and premium ranges to add-ons and FAQs.
                </p>
              </div>
            </div>
            <div className="w-full sm:w-[50%]">
              <img
                className="w-full max-w-full mt-2"
                src="../assets/porcheinsurance.jpeg"
                alt="Porsche Car"
              />
            </div>
          </div>
        </div>
      </div>
      <div className="flex justify-center items-center p-4 mt-6">
        <div className="bg-white shadow-lg rounded-2xl p-6 max-w-7xl text-center">
          <h2 className="text-2xl md:text-3xl font-bold text-gray-800 mb-4">
            About Porsche in India
          </h2>
          <p className="text-gray-600 leading-relaxed">
            Porsche entered the Indian market in 2004 and has since become a top choice for performance and luxury car enthusiasts. Known for their precision German engineering, Porsche vehicles deliver a blend of high-speed dynamics, cutting-edge technology, and timeless design.
          </p>
          <p className="text-gray-600 leading-relaxed mt-3">
            The brand’s India portfolio includes a mix of coupes, SUVs, and electric vehicles, each tailored to a niche but growing base of high-net-worth individuals. Porsche operates via exclusive showrooms and service centers in metro cities like Mumbai, Delhi, Bangalore, and Chennai. Popular models like the Macan, Cayenne, Panamera, and Taycan showcase a mix of power, comfort, and innovation. Given the high cost of parts and repairs, insurance plays a major role in risk mitigation and peace of mind for Porsche owners.
          </p>
        </div>
      </div>
      <div className="p-4 sm:p-8 bg-white">
                                            <h2 className="text-xl sm:text-2xl font-bold mb-6 text-center">
                                              Choose Your Car Brand
                                            </h2>
                                            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
                                              {porscheinsurance.map((calc, index) => (
                                                <motion.div
                                                  key={index}
                                                  whileHover={{ scale: 1.04 }}
                                                  initial={{ opacity: 0, y: 20 }}
                                                  animate={{ opacity: 1, y: 0 }}
                                                  transition={{ duration: 0.3, delay: index * 0.05 }}
                                                >
                                                  <Link
                                                    to={calc.path}
                                                    className="flex flex-col items-center justify-center text-center border border-gray-200 rounded-lg p-5 bg-white shadow-sm hover:shadow-md transition-all duration-300 h-full min-h-[160px]"
                                                  >
                                                    <img
                                                      src={calc.image}
                                                      alt={calc.name}
                                                      className="w-16 h-16 object-contain mb-2"
                                                    />
                                                    <span className="text-md font-medium text-gray-800 break-words leading-snug">
                                                      {calc.name}
                                                    </span>
                                                  </Link>
                                                </motion.div>
                                              ))}
                                            </div>
                                          </div>
      <div className="w-full flex justify-center items-center my-6 px-4">
        <div className="w-full max-w-[984px]">
          <AdBanner displayOrder="2" />
        </div>
      </div>
      <section className="relative py-20 px-4 sm:px-8 bg-gradient-to-br from-white to-gray-100 overflow-hidden">
        <div className="absolute inset-0 z-0 pointer-events-none">
          <div className="absolute top-[-10%] left-[-10%] w-96 h-96 bg-blue-400 opacity-20 blur-3xl rounded-full"></div>
          <div className="absolute bottom-[-10%] right-[-10%] w-96 h-96 bg-purple-400 opacity-20 blur-3xl rounded-full"></div>
        </div>
        <motion.div
          initial={{ opacity: 0, y: 50 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7 }}
          viewport={{ once: true }}
          className="relative z-10 max-w-5xl mx-auto bg-white/90 backdrop-blur-lg rounded-2xl shadow-xl p-10"
        >
          <h2 className="text-3xl md:text-4xl font-bold text-gray-800 mb-6">
            Why Porsche Owners Need Comprehensive Insurance
          </h2>
          <p className="text-gray-700 text-lg leading-relaxed mb-6">
            Porsches are built using high-grade materials, powerful engines, and advanced tech systems that enhance both driving pleasure and safety. These elements also make repairs and replacements extremely costly in the event of damage. Here’s why comprehensive insurance is essential for Porsche owners:
          </p>
          <ul className="grid gap-4 sm:grid-cols-2 md:grid-cols-3 text-gray-700 mb-6">
            <li className="flex items-start gap-3">
              <FaCarCrash className="text-xl text-red-500 mt-1" />
              <span>Expensive bodywork and genuine parts</span>
            </li>
            <li className="flex items-start gap-3">
              <FaUserLock className="text-xl text-yellow-500 mt-1" />
              <span>Complex electronics and drive systems</span>
            </li>
            <li className="flex items-start gap-3">
              <FaCloudSunRain className="text-xl text-blue-500 mt-1" />
              <span>High-risk exposure due to performance driving</span>
            </li>
            <li className="flex items-start gap-3">
              <FaShieldAlt className="text-xl text-green-500 mt-1" />
              <span>Premium paint jobs, interiors, and rims</span>
            </li>
            <li className="flex items-start gap-3">
              <FaTools className="text-xl text-indigo-500 mt-1" />
              <span>Potential liability in case of accidents involving other vehicles or property</span>
            </li>
          </ul>
          <div className="bg-indigo-50 border-l-4 border-indigo-400 p-4 rounded-lg text-indigo-700">
            A strong insurance policy helps cover these aspects and ensures quick recovery from accidents, theft, or unforeseen damages.
          </div>
        </motion.div>
      </section>
      <section className="bg-blue-50 py-6 px-4 sm:px-6 md:px-20 lg:px-20 rounded-3xl shadow-lg max-w-6xl mx-auto mt-10">
        <div className="flex flex-col md:flex-row justify-between items-center gap-10">
          <div className="max-w-xl space-y-6 text-center md:text-left">
            <h2 className="text-2xl sm:text-3xl font-bold text-blue-900 leading-tight">
              Calculate Your Car Insurance <br />
              in <span className="text-blue-600">Seconds</span>
            </h2>
            <p className="text-gray-700 text-lg">
              Use our fast and free calculator to estimate your premium — no paperwork, no waiting.
            </p>
            <Link
              to="/financial-tools-calculators/car-insurance-calculator"
              className="inline-flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white px-6 py-3 text-lg rounded-full shadow-md transition"
            >
              Start Calculating <ArrowRight className="w-5 h-5" />
            </Link>
          </div>
          <div className="space-y-4 text-left">
            {features.map((item, index) => (
              <div key={index} className="flex items-center gap-3">
                <span className="text-xl" role="img" aria-label={item.text}>{item.icon}</span>
                <span className="text-gray-800 font-medium">{item.text}</span>
              </div>
            ))}
          </div>
        </div>
      </section>
      <div className="container mx-auto p-6 mt-2">
        <h2 className="text-2xl md:text-3xl font-semibold text-gray-800 text-center">
          Popular Porsche Models and Insurance Cost Estimates
        </h2>
        <p className="text-gray-600 text-sm md:text-base mt-2 text-center">
          Here’s a snapshot of Porsche models in India, along with estimated Insured Declared Values (IDV) and starting insurance premiums:
        </p>
        <div className="max-w-7xl mx-auto px-4 py-6">
          <div className="block md:hidden">
            {[
              { model: "Macan", category: "Luxury Compact SUV", idv: "90 Lakh to 1.1 Cr", premium: "₹75,000 to ₹1,05,000" },
              { model: "Cayenne", category: "Performance SUV", idv: "1.4 Cr to 1.8 Cr", premium: "₹95,000 to ₹1,40,000" },
              { model: "Panamera", category: "Executive Sedan", idv: "1.6 Cr to 2.1 Cr", premium: "₹1,10,000 to ₹1,60,000" },
              { model: "911", category: "Sports Coupe", idv: "1.9 Cr to 2.6 Cr", premium: "₹1,25,000 to ₹1,90,000" },
              { model: "Taycan", category: "Electric Sedan", idv: "1.6 Cr to 2.3 Cr", premium: "₹1,05,000 to ₹1,50,000" },
            ].map((item, index) => (
              <div key={index} className="border mb-4 p-4 rounded-lg bg-white shadow-md">
                <h3 className="font-semibold text-gray-800 mb-2">{item.model}</h3>
                <div className="text-gray-600 mb-1"><strong>Category:</strong> {item.category}</div>
                <div className="text-gray-600 mb-1"><strong>Approx. IDV:</strong> {item.idv}</div>
                <div className="text-gray-600"><strong>Starting Premium:</strong> {item.premium}</div>
              </div>
            ))}
          </div>
          <div className="hidden md:block overflow-x-auto">
            <table className="min-w-full border-collapse border border-gray-300">
              <thead className="bg-gray-100 text-gray-800">
                <tr>
                  <th className="p-3 border border-gray-300 text-left">Porsche Model</th>
                  <th className="p-3 border border-gray-300 text-left">Segment</th>
                  <th className="p-3 border border-gray-300 text-left">Approx. IDV (₹)</th>
                  <th className="p-3 border border-gray-300 text-left">Starting Premium (₹)</th>
                </tr>
              </thead>
              <tbody className="text-gray-700">
                {[
                  ["Macan", "Luxury Compact SUV", "90 Lakh to 1.1 Cr", "₹75,000 to ₹1,05,000"],
                  ["Cayenne", "Performance SUV", "1.4 Cr to 1.8 Cr", "₹95,000 to ₹1,40,000"],
                  ["Panamera", "Executive Sedan", "1.6 Cr to 2.1 Cr", "₹1,10,000 to ₹1,60,000"],
                  ["911", "Sports Coupe", "1.9 Cr to 2.6 Cr", "₹1,25,000 to ₹1,90,000"],
                  ["Taycan", "Electric Sedan", "1.6 Cr to 2.3 Cr", "₹1,05,000 to ₹1,50,000"],
                ].map(([model, category, idv, premium], index) => (
                  <tr key={index} className="border border-gray-300">
                    <td className="p-3 border border-gray-300">{model}</td>
                    <td className="p-3 border border-gray-300">{category}</td>
                    <td className="p-3 border border-gray-300">{idv}</td>
                    <td className="p-3 border border-gray-300">{premium}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
      <div className="container mx-auto p-6 mt-2">
        <h2 className="text-2xl md:text-3xl font-semibold text-gray-800 text-center">
          Recommended Add-Ons for Porsche Cars
        </h2>
        <p className="text-gray-600 text-sm md:text-base mt-2 text-center">
          Given Porsche’s performance components and luxury detailing, here are key insurance add-ons worth considering:
        </p>
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-3 gap-6 mt-4">
          {cardsNew.map((card, index) => (
            <div
              key={index}
              className="border rounded-lg shadow-lg p-6 flex flex-col items-center text-center"
            >
              {card.icon}
              <h3 className="text-lg font-semibold mb-2">{card.title}</h3>
              <p className="text-gray-600">{card.description}</p>
            </div>
          ))}
        </div>
      </div>
      <div className="w-full flex justify-center items-center my-6 px-4">
        <div className="w-full max-w-[984px]">
          <AdBanner displayOrder="3" />
        </div>
      </div>
      <div className="mx-auto container p-6 mt-6">
        <h2 className="text-2xl md:text-3xl font-semibold text-gray-800 text-center">
          Best Insurance Providers for Porsche Cars
        </h2>
        <p className="text-gray-600 text-sm md:text-base mt-2 text-center">
          Several insurers offer premium plans tailored to high-end luxury vehicles like Porsche. Here are the top choices among owners:
        </p>
        <div className="mt-6 grid md:grid-cols-3 gap-6 text-left">
          <div className="p-6 border rounded-lg shadow-md bg-white">
            <span className="text-3xl">⚡</span>
            <h3 className="text-lg font-semibold text-gray-800 mt-2">HDFC ERGO</h3>
            <p className="text-gray-700 text-sm mt-1">Strong digital tools and coverage customization.</p>
          </div>
          <div className="p-6 border rounded-lg shadow-md bg-white">
            <span className="text-3xl">🚗</span>
            <h3 className="text-lg font-semibold text-gray-800 mt-2">Tata AIG</h3>
            <p className="text-gray-700 text-sm mt-1">Reliable claim settlement and extensive garage network.</p>
          </div>
          <div className="p-6 border rounded-lg shadow-md bg-white">
            <span className="text-3xl">🏥</span>
            <h3 className="text-lg font-semibold text-gray-800 mt-2">ICICI Lombard</h3>
            <p className="text-gray-700 text-sm mt-1">Trusted service quality for luxury brands.</p>
          </div>
          <div className="p-6 border rounded-lg shadow-md bg-white">
            <span className="text-3xl">💰</span>
            <h3 className="text-lg font-semibold text-gray-800 mt-2">Edelweiss</h3>
            <p className="text-gray-700 text-sm mt-1">Known for quick issuance and zero-dep packages.</p>
          </div>
          <div className="p-6 border rounded-lg shadow-md bg-white">
            <span className="text-3xl">📱</span>
            <h3 className="text-lg font-semibold text-gray-800 mt-2">Digit Insurance</h3>
            <p className="text-gray-700 text-sm mt-1">Transparent and easy-to-use app-based services.</p>
          </div>
        </div>
      </div>
      <div className="w-full px-2 py-6 bg-gradient-to-br from-sky-50 via-white to-sky-100 dark:from-gray-900 dark:to-gray-800">
        <div className="max-w-6xl mx-auto grid gap-10">
          <div className="bg-white dark:bg-gray-900 rounded-2xl shadow-lg p-6 md:p-10 border border-sky-200 dark:border-gray-700 hover:shadow-xl transition-shadow">
            <div className="flex items-center gap-3 mb-2">
              <FileText className="w-8 h-8 text-sky-600" />
              <span className="text-2xl md:text-3xl font-bold text-gray-800 dark:text-white">
                How to Save on Porsche Car Insurance
              </span>
            </div>
            <h3 className="text-xl md:text-xl font-bold text-gray-800 dark:text-white mb-6">
              Even with premium vehicles, you can optimize your insurance cost with smart planning. Here are some cost-saving tips:
            </h3>
            <div className="grid sm:grid-cols-2 gap-6 text-gray-700 dark:text-gray-300">
              <div className="p-5 bg-sky-50 dark:bg-gray-800 rounded-xl">
                <span>Compare quotes from at least 3–5 insurers.</span>
              </div>
              <div className="p-5 bg-sky-50 dark:bg-gray-800 rounded-xl">
                <span>Use any applicable No Claim Bonus (NCB) from previous policies.</span>
              </div>
              <div className="p-5 bg-sky-50 dark:bg-gray-800 rounded-xl">
                <span>Install ARAI-approved anti-theft devices for discounts.</span>
              </div>
              <div className="p-5 bg-sky-50 dark:bg-gray-800 rounded-xl">
                <span>Choose a voluntary deductible if you're a confident driver.</span>
              </div>
              <div className="p-5 bg-sky-50 dark:bg-gray-800 rounded-xl">
                <span>Bundle insurance with other assets (home, travel) for loyalty discounts.</span>
              </div>
            </div>
          </div>
          <div className="bg-white dark:bg-gray-900 rounded-2xl shadow-lg p-6 md:p-10 border border-emerald-200 dark:border-gray-700 hover:shadow-xl transition-shadow">
            <div className="flex items-center gap-3 mb-6">
              <BadgeCheck className="w-8 h-8 text-emerald-600" />
              <span className="text-2xl md:text-3xl font-bold text-gray-800 dark:text-white">
                Documents Required for Porsche Car Insurance
              </span>
            </div>
            <h3 className="text-xl md:text-xl font-bold text-gray-800 dark:text-white mb-6">
              Ensure you have the following documents ready while buying or renewing a Porsche insurance policy:
            </h3>
            <div className="grid sm:grid-cols-2 gap-6 text-gray-700 dark:text-gray-300">
              <div className="p-5 bg-emerald-50 dark:bg-gray-800 rounded-xl">
                <span>Vehicle’s Registration Certificate (RC)</span>
              </div>
              <div className="p-5 bg-emerald-50 dark:bg-gray-800 rounded-xl">
                <span>Previous insurance documents (if renewing)</span>
              </div>
              <div className="p-5 bg-emerald-50 dark:bg-gray-800 rounded-xl">
                <span>KYC (Aadhaar/PAN)</span>
              </div>
              <div className="p-5 bg-emerald-50 dark:bg-gray-800 rounded-xl">
                <span>Pollution Under Control (PUC) certificate</span>
              </div>
              <div className="p-5 bg-emerald-50 dark:bg-gray-800 rounded-xl sm:col-span-2">
                <span>Passport-size photograph (as per insurer requirement)</span>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div className="max-w-4xl mx-auto py-6 px-2">
        <h2 className="text-2xl md:text-3xl font-semibold text-center mb-8">
          Porsche Car Insurance FAQs
        </h2>
        <div className="space-y-4">
          {faqs.map((faq, index) => (
            <div
              key={index}
              className="border rounded-lg overflow-hidden shadow-md"
            >
              <button
                onClick={() => toggleAccordion(index)}
                className="w-full p-4 text-left text-lg font-semibold focus:outline-none flex justify-between items-center"
                aria-expanded={activeIndex === index}
                aria-controls={`faq-answer-${index}`}
              >
                {faq.question}
                <Icon id={index} open={activeIndex} />
              </button>
              {activeIndex === index && (
                <div id={`faq-answer-${index}`} className="p-4 bg-gray-100">
                  <p className="text-gray-700">{faq.answer}</p>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
      <section className="relative py-20 px-4 sm:px-8 bg-gradient-to-br from-white to-gray-100 overflow-hidden">
        <div className="absolute inset-0 pointer-events-none z-0">
          <div className="absolute top-[-20%] left-[-10%] w-96 h-96 bg-indigo-300 opacity-30 blur-3xl rounded-full"></div>
          <div className="absolute bottom-[-10%] right-[-10%] w-96 h-96 bg-purple-300 opacity-30 blur-3xl rounded-full"></div>
        </div>
        <motion.div
          initial={{ opacity: 0, y: 60 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7 }}
          viewport={{ once: true }}
          className="relative z-10 max-w-4xl mx-auto bg-white/90 backdrop-blur-sm border border-gray-200 shadow-xl rounded-2xl p-10"
        >
          <div className="flex flex-col md:flex-row items-start md:items-center gap-6">
            <div className="text-indigo-600 text-5xl">
              <FaCarSide />
            </div>
            <div>
              <h2 className="text-3xl md:text-4xl font-bold text-gray-800 mb-4">
                Conclusion
              </h2>
              <p className="text-gray-700 text-lg leading-relaxed mb-4">
                Porsche isn’t just about luxury—it’s about high performance, status, and technological brilliance. Protecting such a premium vehicle with the right insurance policy is not just advisable, it’s essential. With the right plan, you secure your car against risks while enjoying uninterrupted driving pleasure.
              </p>
              <p className="text-gray-700 text-lg leading-relaxed">
                At TheFinMaster, we simplify your insurance decisions with smart comparisons, personalized advice and plans designed to suit high-end vehicles like Porsche. Explore our curated offerings and drive your dream car worry-free.
              </p>
            </div>
          </div>
        </motion.div>
      </section>
    </>
  );
};

export default PorscheCarInsurance;